import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { MentionUser } from '../types';
import Avatar from './Avatar';

interface MentionAutocompleteProps {
  text: string;
  cursorPosition: number;
  users: MentionUser[];
  onSelect: (userId: string, userName: string) => void;
  onClose: () => void;
  containerRef?: React.RefObject<HTMLElement | null>;
}

interface MentionMatch {
  start: number;
  end: number;
  query: string;
}

const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  text,
  cursorPosition,
  users,
  onSelect,
  onClose,
  containerRef,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const mentionMatch = useMemo<MentionMatch | null>(() => {
    let start = cursorPosition - 1;
    while (start >= 0 && text[start] !== '@' && text[start] !== ' ' && text[start] !== '\n') {
      start--;
    }
    if (start < 0 || text[start] !== '@') return null;

    let end = start + 1;
    while (end < text.length && text[end] !== ' ' && text[end] !== '\n' && text[end] !== '@') {
      end++;
    }
    return { start, end, query: text.substring(start + 1, end).toLowerCase() };
  }, [text, cursorPosition]);

  const filteredUsers = useMemo(() => {
    if (!mentionMatch) return [];
    const query = mentionMatch.query.toLowerCase();
    return users
      .filter((user) => {
        const nameMatch = user.name.toLowerCase().includes(query);
        const emailMatch = user.email?.toLowerCase().includes(query);
        return nameMatch || emailMatch;
      })
      .slice(0, 10);
  }, [users, mentionMatch]);

  useEffect(() => {
    const updatePosition = () => {
      if (containerRef?.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setPosition({ left: rect.left, top: rect.top - 10 });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [containerRef, text, cursorPosition]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredUsers.length]);

  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const selectedItem = listRef.current.children[selectedIndex] as HTMLElement;
      selectedItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!mentionMatch || filteredUsers.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredUsers.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredUsers.length) % filteredUsers.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const user = filteredUsers[selectedIndex];
        if (user) onSelect(user.id, user.name);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mentionMatch, filteredUsers, selectedIndex, onSelect, onClose]);

  if (!mentionMatch || filteredUsers.length === 0 || !position) return null;

  return createPortal(
    <div
      ref={listRef}
      className="fixed bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto min-w-[250px]"
      style={{
        maxWidth: '400px',
        left: `${position.left}px`,
        top: `${position.top}px`,
        transform: 'translateY(-100%)',
      }}
    >
      {filteredUsers.map((user, index) => (
        <div
          key={user.id}
          onClick={() => onSelect(user.id, user.name)}
          className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
            index === selectedIndex ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-gray-700'
          }`}
        >
          <Avatar name={user.name} email={user.email} size="sm" />
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-medium truncate">{user.name}</span>
            {user.email && (
              <span className={`text-xs truncate ${index === selectedIndex ? 'text-indigo-100' : 'text-gray-400'}`}>
                {user.email}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>,
    document.body
  );
};

export default MentionAutocomplete;
